/**
 * Document Service - MinIO Document Storage API (TASK-282)
 * Handles document listing, viewing, and downloading from MinIO storage
 *
 * Features:
 * - List documents with filtering by client, type, status
 * - Get presigned URLs for secure browser viewing
 * - Download documents directly
 * - RBAC-protected access
 */

import { httpClient } from '../lib/httpClient';

// ============================================================================
// Types
// ============================================================================

export type DocumentType =
  | 'kyc'
  | 'portfolio'
  | 'risk-profile'
  | 'aml'
  | 'compliance'
  | 'contracts'
  | 'general';

export type DocumentStatus =
  | 'uploaded'
  | 'processing'
  | 'extracted'
  // TASK-817: the extraction ran and gave up. The document is still valid evidence —
  // only the machine reading of it failed, which is why this is not 'rejected'.
  | 'extraction_failed'
  | 'verified'
  | 'rejected'
  | 'archived';

/**
 * TASK-817: what one document's extraction is doing, as something a person can act on.
 * `stalled` is derived server-side from how long the row has been 'processing' — a worker
 * died holding the task, so it is actionable rather than merely slow.
 */
export type ExtractionState = 'extracting' | 'stalled' | 'failed';

export interface DocumentExtractionState {
  id: string;
  filename: string;
  doc_type: string;
  state: ExtractionState;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
  attempts: number;
}

export interface ClientExtractionStatus {
  client_id: string;
  extracting: number;
  stalled: number;
  failed: number;
  documents: DocumentExtractionState[];
  /** When the most recent successful extraction landed; moves when new fields exist. */
  last_extracted_at?: string | null;
}

export interface ClientDocument {
  id: string;
  client_id: string;
  doc_type: DocumentType;
  filename: string;
  original_filename?: string;
  file_size?: number;
  mime_type?: string;
  status: DocumentStatus;
  description?: string;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
  uploaded_by?: string;
  extracted_data?: Record<string, any>;
  extraction_confidence?: number;
  extraction_method?: string;
  // TASK-817
  extraction_started_at?: string | null;
  extraction_completed_at?: string | null;
  extraction_error?: string | null;
  extraction_attempts?: number;
}

export interface DocumentListResponse {
  total: number;
  documents: ClientDocument[];
}

export interface DocumentsByTypeResponse {
  client_id: string;
  total: number;
  documents_by_type: Record<DocumentType, ClientDocument[]>;
  documents: ClientDocument[];
}

export interface PresignedUrlResponse {
  url: string;
  expires_in: number;
  filename: string;
  content_type: string;
}

export interface DocumentFilters {
  client_id?: string;
  doc_type?: DocumentType;
  status?: DocumentStatus;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Document Service
// ============================================================================

class DocumentService {
  // Gateway has dedicated routes for /api/documents (TASK-282)
  private readonly basePath = '/api/documents';

  /**
   * List documents for the account with optional filtering
   */
  async listDocuments(filters?: DocumentFilters): Promise<DocumentListResponse> {
    const params = new URLSearchParams();

    if (filters?.client_id) params.append('client_id', filters.client_id);
    if (filters?.doc_type) params.append('doc_type', filters.doc_type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    return httpClient.get<DocumentListResponse>(url);
  }

  /**
   * Get all documents for a specific client, grouped by type
   */
  async getClientDocuments(
    clientId: string,
    docType?: DocumentType
  ): Promise<DocumentsByTypeResponse> {
    const params = new URLSearchParams();
    if (docType) params.append('doc_type', docType);

    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/client/${clientId}?${queryString}`
      : `${this.basePath}/client/${clientId}`;

    return httpClient.get<DocumentsByTypeResponse>(url);
  }

  /**
   * Get document metadata by ID
   */
  async getDocument(documentId: string): Promise<ClientDocument> {
    return httpClient.get<ClientDocument>(`${this.basePath}/${documentId}`);
  }

  /**
   * Get presigned URL for secure browser viewing/download
   * This is the preferred method for viewing PDFs in the browser
   */
  async getPresignedUrl(
    documentId: string,
    expiresInSeconds: number = 3600
  ): Promise<PresignedUrlResponse> {
    return httpClient.get<PresignedUrlResponse>(
      `${this.basePath}/${documentId}/presigned-url`,
      { params: { expires: expiresInSeconds } }
    );
  }

  /**
   * Download document content directly (proxied through API)
   * For large files, prefer getPresignedUrl() instead
   */
  async downloadDocument(documentId: string): Promise<Blob> {
    return httpClient.get(`${this.basePath}/${documentId}/download`, {
      responseType: 'blob',
    });
  }

  /**
   * Delete a document (soft delete, admin only)
   */
  async deleteDocument(documentId: string): Promise<{ success: boolean; message: string }> {
    return httpClient.delete<{ success: boolean; message: string }>(
      `${this.basePath}/${documentId}`
    );
  }

  /**
   * Helper: Get file icon based on mime type
   */
  getFileIcon(mimeType?: string): string {
    if (!mimeType) return 'file';
    if (mimeType.includes('pdf')) return 'file-text';
    if (mimeType.includes('image')) return 'image';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'table';
    if (mimeType.includes('csv')) return 'table';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'file-text';
    return 'file';
  }

  /**
   * TASK-817: what this client's document reading is doing, right now.
   *
   * Accepting a document starts an extraction that outlives the accept response by tens of
   * seconds. Without this, a KYC workspace opened during one could only count the fields
   * that had arrived — none — and told the user to go request the evidence from the client.
   */
  async getClientExtractionStatus(clientId: string): Promise<ClientExtractionStatus> {
    return httpClient.get<ClientExtractionStatus>(
      `${this.basePath}/client/${clientId}/extraction-status`
    );
  }

  /**
   * TASK-817: re-run extraction for a document whose last attempt failed or was abandoned.
   * Rejects with 409 if an extraction is genuinely still running.
   */
  async retryExtraction(documentId: string): Promise<{
    success: boolean;
    document_id: string;
    extraction_status: string;
  }> {
    return httpClient.post(`${this.basePath}/${documentId}/extract`, {});
  }

  /**
   * Helper: Format file size for display
   */
  formatFileSize(bytes?: number): string {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  /**
   * Helper: Get document type display name
   */
  getDocTypeDisplayName(docType: DocumentType): string {
    const names: Record<DocumentType, string> = {
      kyc: 'KYC Document',
      portfolio: 'Portfolio',
      'risk-profile': 'Risk Profile',
      aml: 'AML Document',
      compliance: 'Compliance',
      contracts: 'Contract',
      general: 'General',
    };
    return names[docType] || docType;
  }

  /**
   * TASK-817: Human label for a document status.
   *
   * The raw enum member was rendered directly in the archive table, which was survivable
   * while every value was a single word. 'extraction_failed' is not, and more importantly
   * 'uploaded' and 'processing' do not say what they mean to someone who just accepted a
   * document — one is "waiting", the other is "we are reading it".
   */
  getStatusLabel(status: DocumentStatus): string {
    const labels: Record<DocumentStatus, string> = {
      uploaded: 'Uploaded',
      processing: 'Reading…',
      extracted: 'Read',
      extraction_failed: "Couldn't read",
      verified: 'Verified',
      rejected: 'Rejected',
      archived: 'Archived',
    };
    return labels[status] || status;
  }

  /**
   * Helper: Get status badge color
   */
  getStatusColor(status: DocumentStatus): string {
    const colors: Record<DocumentStatus, string> = {
      uploaded: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      extracted: 'bg-green-100 text-green-800',
      extraction_failed: 'bg-amber-100 text-amber-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      archived: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
}

export const documentService = new DocumentService();
export default documentService;
